// Single source of truth for the intake flow's step type and order.
// FlowStepRenderer re-exports FlowStep so existing imports don't need to change.

export type FlowStep =
  | 'welcome'
  | 'state-check'
  | 'login'
  | 'email-capture'
  | 'intro'
  | 'health-questions-intro'
  | 'questionnaire'
  | 'questionnaire-summary'
  | 'dose-selection'
  | 'quantity-selection'
  | 'subscription-selection'
  | 'account-details'
  | 'shipping-info'
  | 'file-upload'
  | 'cart-summary'
  | 'checkout'
  | 'complete';

export const FLOW_ORDER: readonly FlowStep[] = [
  'welcome',
  'intro',
  'state-check',
  'email-capture',
  'health-questions-intro',
  'questionnaire',
  'questionnaire-summary',
  'dose-selection',
  'quantity-selection',
  'subscription-selection',
  'account-details',
  'shipping-info',
  'file-upload',
  'cart-summary',
  'checkout',
  'complete',
];

/** SRX-202: the questionnaire pauses to capture email right after this many questions
 *  have been answered (counting the synthetic medication-check pre-question as #1),
 *  instead of asking for it before the questionnaire begins. */
export const EMAIL_CAPTURE_AFTER_QUESTION_COUNT = 3;

interface AccountDataForSkipCheck {
  customer_id?: string;
  has_mdi_account?: boolean;
}

interface FlowStateForSkipCheck {
  accountData?: AccountDataForSkipCheck | null;
}

/** True once a customer already has both a Shopify and an MDI account — account-details is skipped for them. */
export function shouldSkipAccountDetails(flowState: FlowStateForSkipCheck): boolean {
  return !!(flowState.accountData?.customer_id && flowState.accountData?.has_mdi_account);
}

/**
 * Walks FLOW_ORDER by one position, skipping the conditional `account-details` step
 * when `shouldSkipAccountDetails(flowState)` is true. Use this for both the
 * `subscription-selection` "continue" handler and the `shipping-info` "back" handler
 * so forward/back navigation stays symmetric.
 */
export function getAdjacentStep(
  current: FlowStep,
  direction: 1 | -1,
  flowState: FlowStateForSkipCheck
): FlowStep {
  const idx = FLOW_ORDER.indexOf(current);
  if (idx === -1) return current;

  let nextIdx = idx + direction;
  while (FLOW_ORDER[nextIdx] === 'account-details' && shouldSkipAccountDetails(flowState)) {
    nextIdx += direction;
  }
  return FLOW_ORDER[nextIdx] ?? current;
}

/**
 * SRX-198 moved password/account creation to account-details' `access` sub-step, right before
 * shipping-info. A brand-new registrant has no token (and so no possibility of remote resume)
 * for the entire journey up to that point anyway, so remote progress persistence
 * (/apps/rx/progress/get and /apps/rx/progress/update) is only meaningful from shipping-info
 * onward — once account registration has happened, the customer has a real Shopify session, so a
 * page refresh can't fall back to "start fresh, re-enter your email" the way it can earlier on.
 * Without saved progress here they'd be stuck logged in with nothing to resume into. Every call
 * site that touches /apps/rx/progress/get or /update still has to fire the request itself
 * (there's no way to know in advance whether saved progress is past that point without asking) —
 * this is what restricts whether the result is *acted on*.
 */
const POST_ACCOUNT_CREATION_STEPS: readonly FlowStep[] = [
  'shipping-info',
  'file-upload',
  'cart-summary',
  'checkout',
  'complete',
];

/** True once a step is at or after shipping-info — see POST_ACCOUNT_CREATION_STEPS above. */
export function isPastAccountCreationStep(step: FlowStep): boolean {
  return POST_ACCOUNT_CREATION_STEPS.includes(step);
}
